// GUS-88 Clinics Exam Builder — shared shapes for the builder, the exam
// renderer and the print page.

export type ClinicFieldType =
  | 'header'
  | 'text'
  | 'yesno'
  | 'number'
  | 'select'
  | 'picklist'
  | 'table'
  | 'summary';

export interface ClinicFieldTableRow {
  label: string;
}

export interface ClinicFieldConfig {
  minHeight?: string;
  unit?: string;
  options?: string[];
  left?: string;
  right?: string;
  leftValue?: number;
  rightValue?: number;
  comment?: boolean;
  rows?: ClinicFieldTableRow[];
  multi?: boolean;
}

export interface ClinicExamAutoText {
  Content: string;
}

export interface ClinicExamField {
  FieldID?: number;
  FieldType: ClinicFieldType;
  Label: string;
  Width: string;
  SortOrder?: number;
  /** stored as a JSON string in the DB, kept as an object in the client */
  Config: ClinicFieldConfig;
  autoTexts: ClinicExamAutoText[];
  /** set by the exam-scoped endpoint: removed from the template but still filled here */
  Retired?: boolean;
  IsActive?: boolean;
}

export interface ClinicExamTemplate {
  ClinicTemplateID?: number;
  Title: string;
  TemplateKind?: string;
  FieldCount?: number;
  assignedUIDs: any[];
  fields?: ClinicExamField[];
  inactiveFields?: ClinicExamField[];
}

export const CLINIC_FIELD_WIDTHS = ['w100', 'w50', 'w33', 'w25'];

export interface ClinicFieldTypeMeta {
  type: ClinicFieldType;
  label: string;
  /** does this field carry a free-text value that an Auto Text list can fill? */
  hasAutoText: boolean;
  defaultLabel: string;
  defaultConfig: ClinicFieldConfig;
}

/** The palette, in the order it is offered to the admin. */
export const CLINIC_FIELD_TYPES: ClinicFieldTypeMeta[] = [
  {
    type: 'header',
    label: 'כותרת ',
    hasAutoText: false,
    defaultLabel: 'כותרת',
    defaultConfig: {},
  },
  {
    type: 'text',
    label: 'תיבת טקסט',
    hasAutoText: true,
    defaultLabel: 'שדה טקסט',
    defaultConfig: { minHeight: '10em' },
  },
  {
    type: 'yesno',
    label: 'כן / לא + הערה',
    hasAutoText: true,
    defaultLabel: 'תקין / לא תקין',
    defaultConfig: {
      left: 'Normal',
      right: 'Abnormal',
      leftValue: 0,
      rightValue: 1,
      comment: true,
    },
  },
  {
    type: 'number',
    label: 'מספר + יחידה',
    hasAutoText: false,
    defaultLabel: 'מידה',
    defaultConfig: { unit: 'cm' },
  },
  {
    type: 'select',
    label: 'רשימת בחירה',
    hasAutoText: false,
    defaultLabel: 'בחירה',
    defaultConfig: { options: [] },
  },
  {
    type: 'picklist',
    label: 'רשימת פריטים',
    hasAutoText: true,
    defaultLabel: 'רשימה',
    defaultConfig: { multi: true },
  },
  {
    type: 'table',
    label: 'טבלה',
    hasAutoText: false,
    defaultLabel: 'טבלה',
    defaultConfig: { rows: [], left: 'Normal', right: 'Abnormal', comment: true },
  },
  {
    type: 'summary',
    label: 'טקסט  (יכולות מתקדמות)',
    hasAutoText: true,
    defaultLabel: 'Summary of Exam',
    defaultConfig: {},
  },
];

export function fieldTypeMeta(type: ClinicFieldType): ClinicFieldTypeMeta {
  return CLINIC_FIELD_TYPES.find((t) => t.type === type) || CLINIC_FIELD_TYPES[1];
}

/** The DB stores Config as a JSON string; tolerate null/garbage. */
export function parseConfig(raw: any): ClinicFieldConfig {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw) || {};
  } catch (e) {
    return {};
  }
}

/**
 * A fresh copy of a type's defaults. Must be a deep clone: defaultConfig holds
 * arrays (options, rows) and handing out the module-level ones would let two
 * fields edit the same array.
 */
export function cloneDefaultConfig(type: ClinicFieldType): ClinicFieldConfig {
  return JSON.parse(JSON.stringify(fieldTypeMeta(type).defaultConfig));
}

/**
 * Fills in any config key the stored JSON is missing, so a field authored
 * before a key existed (or hand-edited in the DB) still renders and, for the
 * toggles, still matches its radio values.
 */
export function normalizeField(raw: any): ClinicExamField {
  return {
    ...raw,
    Label: raw.Label || '',
    Width: raw.Width || 'w100',
    Config: { ...cloneDefaultConfig(raw.FieldType), ...parseConfig(raw.Config) },
    autoTexts: raw.autoTexts || [],
  };
}

/** Control name for a field's value slot. Keyed by FieldID so answers survive template edits. */
export function controlName(field: ClinicExamField, valueKey = ''): string {
  return 'f' + field.FieldID + (valueKey ? '_' + valueKey : '');
}
